import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

// Os destinos das ligações ficam no código (não se traduzem); os textos vêm do
// catálogo na mesma ordem — migrantes.violencia.genero.accordions[i].links[j].
const ACCORDION_HREFS = [
    ['http://precisodeajuda.pt/vd'],
    ['https://precisodeajuda.pt/crimes-sexuais/', 'http://apav.pt/care'],
    [],
]

function Accordion({ items }) {
    const [open, setOpen] = useState(null)
    return (
        <div className="space-y-2 mt-4">
            {items.map((item, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                        onClick={() => setOpen(open === i ? null : i)}
                        className="w-full flex items-center justify-between px-6 py-4 text-start font-bold text-slate-800 hover:bg-slate-50 transition-colors"
                        aria-expanded={open === i}
                        aria-controls={`acordeao-${i}`}
                        id={`acordeao-botao-${i}`}
                    >
                        <span>{item.title}</span>
                        <ChevronDown
                            size={20}
                            aria-hidden="true"
                            className={`text-brand-600 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
                        />
                    </button>
                    <AnimatePresence>
                        {open === i && (
                            <motion.div
                                id={`acordeao-${i}`}
                                role="region"
                                aria-labelledby={`acordeao-botao-${i}`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                            >
                                <div className="px-6 pb-5 text-slate-700 leading-relaxed space-y-3">
                                    <p>{item.content}</p>
                                    {item.links.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            {item.links.map((l, j) => (
                                                <a key={j} href={l.href} target="_blank" rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline">
                                                    {l.text} <ExternalLink size={14} aria-hidden="true" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            ))}
        </div>
    )
}

export default function PessoasMigrantes() {
    const { t } = useTranslation()
    const accordions = t('migrantes.violencia.genero.accordions', { returnObjects: true }).map((item, i) => ({
        ...item,
        links: (item.links || []).map((text, j) => ({ text, href: ACCORDION_HREFS[i]?.[j] })),
    }))

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head> */}
            <title>{t('migrantes.metaTitle')}</title>
            <meta name="description" content={t('migrantes.metaDescription')} />
            <link rel="canonical" href={pageUrl('pessoas-migrantes')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('migrantes.title')}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Vulnerabilidade */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{t('migrantes.vulnerabilidade.title')}</h2>
                        <div className="space-y-4 text-slate-700 leading-relaxed text-lg">
                            <p>{t('migrantes.vulnerabilidade.p1')}</p>
                            <p>{t('migrantes.vulnerabilidade.p2')}</p>
                            <p>{t('migrantes.vulnerabilidade.p3')}</p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Violência contra pessoas migrantes */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{t('migrantes.violencia.title')}</h2>
                        <p className="text-slate-700 leading-relaxed text-lg mb-10">
                            {t('migrantes.violencia.intro')}
                        </p>

                        {/* Violência baseada no género */}
                        <div className="mb-10">
                            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="text-brand-600" aria-hidden="true">&gt;</span> {t('migrantes.violencia.genero.title')}
                            </h3>
                            <p className="text-slate-700 leading-relaxed text-base mb-4">
                                {t('migrantes.violencia.genero.text')}
                            </p>
                            <Accordion items={accordions} />
                        </div>

                        {/* Exploração e Tráfico de Pessoas */}
                        <div className="mb-10">
                            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="text-brand-600" aria-hidden="true">&gt;</span> {t('migrantes.violencia.trafico.title')}
                            </h3>
                            <div className="space-y-4 text-slate-700 leading-relaxed text-base">
                                <p>{t('migrantes.violencia.trafico.p1')}</p>
                                <p>{t('migrantes.violencia.trafico.p2')}</p>
                            </div>
                        </div>

                        {/* Discriminação e Crimes de Ódio */}
                        <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="text-brand-600" aria-hidden="true">&gt;</span> {t('migrantes.violencia.discriminacao.title')}
                            </h3>
                            <div className="space-y-4 text-slate-700 leading-relaxed text-base mb-4">
                                <p>{t('migrantes.violencia.discriminacao.p1')}</p>
                                <p>{t('migrantes.violencia.discriminacao.p2')}</p>
                            </div>
                            <a
                                href="http://precisodeajuda.pt/discriminacao"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline"
                            >
                                {t('migrantes.violencia.discriminacao.link')} <ExternalLink size={14} aria-hidden="true" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
